import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { Button } from './ui/button';
import { HUDMeters } from './HUDMeters';
import { PrepCardSummary } from './PrepCardBuilder';
import { getPathNarrative } from '../data/pathNarratives';
import { 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  FileText,
  Target,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  BookOpen,
  Shield,
  Users,
  Lightbulb,
  Scale,
} from 'lucide-react';

// Animation variants
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};

const OUTCOME_CONFIG = {
  A: {
    title: 'Trust Restored, Action Clear',
    icon: CheckCircle,
    color: '#78BE20',
    bgColor: 'bg-[#78BE20]/10',
    borderColor: 'border-l-[#78BE20]',
  },
  B: {
    title: 'Productive but Incomplete',
    icon: AlertTriangle,
    color: '#00A9E0',
    bgColor: 'bg-[#00A9E0]/10',
    borderColor: 'border-l-[#00A9E0]',
  },
  C: {
    title: 'Relationship Preserved, Accountability Soft',
    icon: AlertTriangle,
    color: '#FFB81C',
    bgColor: 'bg-[#FFB81C]/10',
    borderColor: 'border-l-[#FFB81C]',
  },
  D: {
    title: 'Compliance Without Buy-In',
    icon: AlertTriangle,
    color: '#FFB81C',
    bgColor: 'bg-[#FFB81C]/10',
    borderColor: 'border-l-[#FFB81C]',
  },
  E: {
    title: 'Escalation Increased',
    icon: AlertTriangle,
    color: '#DA291C',
    bgColor: 'bg-[#DA291C]/10',
    borderColor: 'border-l-[#DA291C]',
  },
};

// PSEL Standards relevant to leadership conversations
const PSEL_STANDARDS = [
  {
    id: 2,
    label: 'PSEL 2',
    title: 'Ethics & Professional Norms',
    icon: Scale,
    description: 'Act ethically and according to professional norms to promote each student\'s academic success and well-being.',
    indicators: [
      { text: 'Invited perspective before drawing conclusions', meter: 'trust', threshold: 55, above: true },
      { text: 'Addressed the concern without blame-heavy language', meter: 'escalationRisk', threshold: 60, above: false },
      { text: 'Kept professional norms intact under pressure', meter: 'efficacy', threshold: 45, above: true },
    ],
  },
  {
    id: 5,
    label: 'PSEL 5',
    title: 'Community of Care & Support',
    icon: Users,
    description: 'Promote the current and future success and well-being of each student.',
    indicators: [
      { text: 'Named student impact explicitly', meter: 'clarity', threshold: 55, above: true },
      { text: 'Avoided minimizing student experience', meter: 'clarity', threshold: 45, above: true },
    ],
  },
  {
    id: 6,
    label: 'PSEL 6',
    title: 'Professional Capacity of Personnel',
    icon: BookOpen,
    description: 'Develop the professional capacity and practice of school personnel.',
    indicators: [
      { text: 'Supported teacher growth over compliance', meter: 'efficacy', threshold: 50, above: true },
      { text: 'Created space for professional reflection', meter: 'trust', threshold: 50, above: true },
    ],
  },
  {
    id: 7,
    label: 'PSEL 7',
    title: 'Professional Community',
    icon: Shield,
    description: 'Foster a professional community of teachers and staff that promotes mutual trust.',
    indicators: [
      { text: 'Built collaborative resolution over directive', meter: 'trust', threshold: 55, above: true },
      { text: 'Kept escalation risk under control', meter: 'escalationRisk', threshold: 55, above: false },
    ],
  },
  {
    id: 10,
    label: 'PSEL 10',
    title: 'School Improvement',
    icon: Lightbulb,
    description: 'Act as agents of continuous improvement to promote each student\'s success.',
    indicators: [
      { text: 'Established clear next steps', meter: 'clarity', threshold: 55, above: true },
      { text: 'Created path for ongoing development', meter: 'efficacy', threshold: 50, above: true },
    ],
  },
];

export function ImpactDashboard() {
  const { state, resetSimulation, setView } = useSimulation();
  const { outcome, debrief, meters, simulation } = state;

  const [showPSEL, setShowPSEL] = useState(false);

  if (!outcome || !debrief) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-400">Loading dashboard...</p>
      </div>
    );
  }

  const outcomeConfig = OUTCOME_CONFIG[outcome];
  const OutcomeIcon = outcomeConfig.icon;

  const handleRestart = () => {
    resetSimulation();
    setView('hub');
  };

  // Calculate meter trends
  const getMeterTrend = (meterName) => {
    if (simulation.meterHistory.length < 2) return null;
    const initial = simulation.meterHistory[0][meterName];
    const final = meters[meterName];
    const diff = final - initial;
    return { diff, direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'neutral' };
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="impact-dashboard">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        className="bg-[#002B45] text-white py-8 sm:py-12 px-4 sm:px-8 lg:px-16"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#00A9E0]" strokeWidth={1.5} />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#00A9E0]">
              Reflect
            </span>
            <span className="mx-1 text-xs text-slate-500">|</span>
            <span className="text-xs font-semibold uppercase tracking-widest text-[#00A9E0]">
              Conversation Debrief
            </span>
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-headline font-bold tracking-tight mb-4">
            Simulation Complete
          </h1>
          
          <p className="text-lg text-slate-300 max-w-2xl">
            Review your performance, analyze decision patterns, and identify areas for growth.
          </p>
        </div>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-8 sm:py-12"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6 lg:space-y-8">
            {/* Outcome Card */}
            <motion.div
              variants={fadeUp}
              className={`p-4 sm:p-6 lg:p-8 bg-white shadow-ambient border-l-4 ${outcomeConfig.borderColor}`}
              data-testid="outcome-card"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 ${outcomeConfig.bgColor}`}>
                  <OutcomeIcon 
                    className="w-6 h-6" 
                    style={{ color: outcomeConfig.color }}
                    strokeWidth={1.5}
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                      Outcome {outcome}
                    </span>
                  </div>
                  
                  <h2 className="text-2xl font-headline font-bold text-[#002B45] mb-4">
                    {outcomeConfig.title}
                  </h2>
                  
                  <p className="text-base text-[#475569] leading-relaxed mb-4">
                    {debrief.summary}
                  </p>
                  
                  <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                    <span className="text-xs font-medium text-slate-400">Final State:</span>
                    <span className="text-sm font-semibold text-[#002B45]">
                      {debrief.finalState}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Readiness + Decision Quality */}
            <motion.div variants={fadeUp} className="bg-white p-4 sm:p-6 lg:p-8 shadow-ambient" data-testid="readiness-quality">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Overall Readiness */}
                <div className="p-4 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    Overall Readiness
                  </p>
                  <div className="flex items-end gap-2 mb-2">
                    <span className={`text-3xl font-bold font-headline ${
                      debrief.overallReadiness >= 70 ? 'text-[#78BE20]' :
                      debrief.overallReadiness >= 45 ? 'text-[#FFB81C]' :
                      'text-[#DA291C]'
                    }`}>
                      {debrief.overallReadiness}
                    </span>
                    <span className="text-xs text-slate-400 pb-1">/ 100</span>
                  </div>
                  <p className="text-xs text-[#475569]">
                    {debrief.overallReadiness >= 70
                      ? 'Strong foundation for a productive conversation.'
                      : debrief.overallReadiness >= 45
                        ? 'Reasonable start, but some areas need attention.'
                        : 'Key conditions were not met for a productive outcome.'}
                  </p>
                </div>

                {/* Decision Quality */}
                <div className="p-4 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    Decision Quality
                  </p>
                  <div className="flex items-end gap-2 mb-2">
                    <span className={`text-3xl font-bold font-headline ${
                      debrief.decisionQuality >= 70 ? 'text-[#78BE20]' :
                      debrief.decisionQuality >= 40 ? 'text-[#FFB81C]' :
                      'text-[#DA291C]'
                    }`}>
                      {debrief.strongChoices}/{debrief.totalChoices}
                    </span>
                    <span className="text-xs text-slate-400 pb-1">strong moves</span>
                  </div>
                  <p className="text-xs text-[#475569]">
                    {debrief.decisionQuality >= 70
                      ? 'Your choices consistently advanced trust and clarity.'
                      : debrief.decisionQuality >= 40
                        ? 'Some strong choices, but key moments were missed.'
                        : 'Most choices did not align with productive conversation patterns.'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Meter Analysis */}
            <motion.div variants={fadeUp} className="bg-white p-8 shadow-ambient" data-testid="meter-analysis">
              <h3 className="text-lg font-headline font-semibold text-[#002B45] mb-6">
                Conversation Conditions
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {['trust', 'efficacy', 'clarity', 'escalationRisk'].map((meterName) => {
                  const trend = getMeterTrend(meterName);
                  const labels = {
                    trust: 'Trust',
                    efficacy: 'Efficacy',
                    clarity: 'Clarity',
                    escalationRisk: 'Escalation Risk',
                  };
                  
                  return (
                    <div key={meterName} className="p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#002B45]">
                          {labels[meterName]}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold font-headline text-[#002B45]">
                            {meters[meterName]}
                          </span>
                          {trend && (
                            <span className={`flex items-center text-xs font-semibold ${
                              meterName === 'escalationRisk'
                                ? (trend.diff > 0 ? 'text-[#DA291C]' : 'text-[#78BE20]')
                                : (trend.diff > 0 ? 'text-[#78BE20]' : 'text-[#DA291C]')
                            }`}>
                              {trend.direction === 'up' ? (
                                <TrendingUp className="w-3 h-3 mr-1" />
                              ) : trend.direction === 'down' ? (
                                <TrendingDown className="w-3 h-3 mr-1" />
                              ) : null}
                              {trend.diff > 0 ? '+' : ''}{trend.diff}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-2 w-full bg-slate-200 overflow-hidden">
                        <div 
                          className={`h-full ${
                            meterName === 'escalationRisk'
                              ? (meters[meterName] > 60 ? 'bg-[#DA291C]' : 'bg-[#78BE20]')
                              : (meters[meterName] < 40 ? 'bg-[#DA291C]' : 'bg-[#0055A5]')
                          }`}
                          style={{ width: `${meters[meterName]}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Decision Path */}
            <motion.div variants={fadeUp} className="bg-white p-4 sm:p-6 lg:p-8 shadow-ambient" data-testid="decision-path">
              <h3 className="text-lg font-headline font-semibold text-[#002B45] mb-2">
                Your Conversation Path
              </h3>
              <p className="text-sm text-slate-400 mb-8">
                This view shows how your choices shaped the conversation from opening move to closure.
              </p>
              
              <div className="relative">
                {/* Vertical connector line */}
                <div className="absolute left-[15px] top-4 bottom-4 w-px bg-slate-200" />

                <div className="space-y-0">
                  {debrief.pathHistory.map((nodeId, index) => {
                    const narrative = getPathNarrative(nodeId);
                    const qualityColor = narrative.quality === 'strong'
                      ? 'bg-[#78BE20]'
                      : narrative.quality === 'weak'
                        ? 'bg-[#DA291C]'
                        : narrative.quality === 'mixed'
                          ? 'bg-[#FFB81C]'
                          : 'bg-[#0055A5]';
                    const isLast = index === debrief.pathHistory.length - 1;

                    return (
                      <div key={index} data-testid={`path-step-${index}`}>
                        <div className="flex gap-4 relative">
                          {/* Step marker */}
                          <div className={`relative z-10 w-8 h-8 flex-shrink-0 flex items-center justify-center ${qualityColor}`}>
                            <span className="text-xs font-bold text-white">
                              {index + 1}
                            </span>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 pb-6">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                              {narrative.label}
                            </p>
                            <p className="text-sm text-[#002B45] leading-relaxed">
                              {narrative.description}
                            </p>
                          </div>
                        </div>

                        {/* Connector text */}
                        {!isLast && narrative.connector && (
                          <div className="ml-12 pb-4">
                            <p className="text-xs italic text-slate-400 leading-relaxed border-l-2 border-slate-100 pl-3">
                              {narrative.connector}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Mismatches Summary */}
            {/* Top Coach Signal + Next Practice */}
            <motion.div variants={fadeUp} className="bg-white p-4 sm:p-6 lg:p-8 shadow-ambient" data-testid="practice-recommendation">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Top Coach Signal */}
                <div className={`p-4 border-l-[3px] ${
                  debrief.topCoachSignal?.type === 'hard'
                    ? 'border-l-[#DA291C] bg-[#DA291C]/[0.03]'
                    : debrief.topCoachSignal
                      ? 'border-l-[#FFB81C] bg-[#FFB81C]/[0.03]'
                      : 'border-l-[#78BE20] bg-[#78BE20]/[0.03]'
                }`}>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    Top Coach Signal
                  </p>
                  <p className="text-sm font-medium text-[#002B45] mb-1">
                    {debrief.topCoachSignal
                      ? debrief.topCoachSignal.trigger
                      : 'No critical mismatches detected'}
                  </p>
                  <p className="text-xs text-[#475569]">
                    {debrief.topCoachSignal
                      ? 'This was the most significant drift from your stated plan.'
                      : 'Your live choices stayed close to the approach you planned.'}
                  </p>
                </div>

                {/* Next Practice Recommendation */}
                <div className="p-4 bg-[#0055A5]/[0.03] border-l-[3px] border-l-[#0055A5]">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
                    Next Practice Focus
                  </p>
                  <p className="text-sm font-medium text-[#002B45] mb-1">
                    {debrief.nextPractice.area} ({debrief.nextPractice.value}/100)
                  </p>
                  <p className="text-xs text-[#475569]">
                    {debrief.nextPractice.recommendation}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Final Meters */}
            <div className="bg-white p-6 shadow-ambient">
              <HUDMeters />
            </div>
            
            {/* Plan Summary */}
            <PrepCardSummary />

            {/* Plan vs. Live Choices */}
            {(debrief.mismatches.hard.length > 0 || debrief.mismatches.soft.length > 0) && (
              <div className="bg-white p-5 shadow-ambient" data-testid="mismatches-summary">
                <h3 className="text-sm font-headline font-semibold text-[#002B45] mb-4">
                  Plan vs. Live Choices
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#DA291C]/5 border-l-2 border-l-[#DA291C]">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#DA291C] mb-1">
                      Hard
                    </p>
                    <p className="text-2xl font-bold font-headline text-[#002B45]">
                      {debrief.mismatches.hard.length}
                    </p>
                  </div>
                  <div className="p-3 bg-[#FFB81C]/5 border-l-2 border-l-[#FFB81C]">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#FFB81C] mb-1">
                      Soft
                    </p>
                    <p className="text-2xl font-bold font-headline text-[#002B45]">
                      {debrief.mismatches.soft.length}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Plan Alignment</span>
                    <span className={`text-sm font-bold font-headline ${
                      debrief.prepCardAlignment >= 80 ? 'text-[#78BE20]' :
                      debrief.prepCardAlignment >= 50 ? 'text-[#FFB81C]' :
                      'text-[#DA291C]'
                    }`}>
                      {debrief.prepCardAlignment}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* PSEL Standards — compact sidebar version */}
            <div className="bg-white shadow-ambient" data-testid="psel-overlay">
              <button
                onClick={() => setShowPSEL(!showPSEL)}
                className="w-full flex items-center justify-between p-5 hover:bg-slate-50/50 transition-colors"
                data-testid="psel-toggle"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-[#002B45] flex items-center justify-center">
                    <Scale className="w-3 h-3 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-headline font-semibold text-[#002B45]">
                      Standards Alignment
                    </h3>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showPSEL ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showPSEL && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-4">
                      {/* Legend */}
                      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 bg-[#78BE20]" />
                          <span className="text-[10px] text-slate-500">Strong</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 bg-[#FFB81C]" />
                          <span className="text-[10px] text-slate-500">Emerging</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 bg-[#DA291C]" />
                          <span className="text-[10px] text-slate-500">Needs Practice</span>
                        </div>
                      </div>

                      {PSEL_STANDARDS.map((standard) => {
                        const results = standard.indicators.map(indicator => {
                          const value = meters[indicator.meter];
                          const met = indicator.above 
                            ? value >= indicator.threshold 
                            : value < indicator.threshold;
                          return { ...indicator, met, value };
                        });
                        const allMet = results.every(r => r.met);
                        const noneMet = results.every(r => !r.met);
                        const statusColor = allMet ? '#78BE20' : noneMet ? '#DA291C' : '#FFB81C';
                        const StdIcon = standard.icon;

                        return (
                          <div key={standard.id} className="py-2">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-5 h-5 flex items-center justify-center" style={{ backgroundColor: `${statusColor}15` }}>
                                <StdIcon className="w-3 h-3" style={{ color: statusColor }} strokeWidth={1.5} />
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-[#002B45]">
                                  {standard.label}: {standard.title}
                                </p>
                              </div>
                            </div>
                            <div className="ml-7 space-y-1">
                              {results.map((r, idx) => (
                                <div key={idx} className="flex items-start gap-1.5">
                                  <span className={`w-1.5 h-1.5 mt-1 flex-shrink-0 ${r.met ? 'bg-[#78BE20]' : 'bg-[#DA291C]'}`} />
                                  <span className="text-[11px] text-[#475569] leading-tight">{r.text}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleRestart}
                className="w-full bg-[#0055A5] hover:bg-[#002B45] text-white py-4"
                data-testid="new-simulation"
              >
                <Target className="w-4 h-4 mr-2" />
                New Simulation
              </Button>
              
              <Button
                onClick={() => setView('hub')}
                variant="ghost"
                className="w-full text-slate-500 hover:text-[#002B45]"
                data-testid="return-hub"
              >
                Return to Hub
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default ImpactDashboard;
